import React, { useState, useEffect, useCallback } from "react";
import { dealsAPI, dashboardAPI } from "../utils/api";
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { FileText, Download, TrendingUp, TrendingDown } from "lucide-react";

const Reports = () => {
  const [activeReport, setActiveReport] = useState("pipeline");
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    try {
      const [chartsRes, dealsRes] = await Promise.all([
        dashboardAPI.getCharts({ year }),
        dealsAPI.getAll({ year })
      ]);
      setReportData({
        charts: chartsRes.data,
        deals: dealsRes.data
      });
    } catch (error) {
      console.error("Error fetching report data:", error);
    } finally {
      setLoading(false);
    }
  }, [activeReport, year]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const reports = [
    { id: "pipeline", name: "Pipeline Report" },
    { id: "winloss", name: "Win/Loss Analysis" },
    { id: "revenue", name: "Revenue Analysis" },
    { id: "forecast", name: "Forecast" },
    { id: "yoy", name: "YoY Comparison" },
    { id: "activity", name: "BD Activity" },
    { id: "client", name: "Client Analysis" }
  ];

  const COLORS = ["#2563EB", "#10B981", "#0EA5E9", "#F59E0B", "#EF4444"];

  const formatCurrency = (value) => `RM ${(value / 1000).toFixed(0)}K`;

  const renderPipelineReport = () => {
    if (!reportData) return null;
    const { pipeline_by_stage } = reportData.charts;

    return (
      <div className="space-y-6" data-testid="pipeline-report">
        <div>
          <h3 className="text-xl font-semibold text-slate-900 mb-4">Pipeline by Stage</h3>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={pipeline_by_stage}>
              <XAxis dataKey="stage" />
              <YAxis tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`} />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Bar dataKey="value" fill="#2563EB" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {pipeline_by_stage && pipeline_by_stage.map((stage) => (
            <div key={stage.stage} className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-slate-600 mb-1">{stage.stage}</p>
              <p className="text-xl font-bold text-slate-900">{formatCurrency(stage.value)}</p>
              <p className="text-sm text-slate-500">{stage.count} deals</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderWinLossReport = () => {
    if (!reportData) return null;
    const { deals } = reportData;

    const won = deals.filter(d => d.stage === "Won");
    const lost = deals.filter(d => d.stage === "Lost");
    const dropped = deals.filter(d => d.stage === "Dropped");
    const total = won.length + lost.length + dropped.length;
    const winRate = total > 0 ? ((won.length / total) * 100).toFixed(1) : 0;

    const data = [
      { name: "Won", value: won.length, fill: "#10B981" },
      { name: "Lost", value: lost.length, fill: "#EF4444" },
      { name: "Dropped", value: dropped.length, fill: "#64748B" }
    ];

    return (
      <div className="space-y-6" data-testid="winloss-report">
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <p className="text-sm text-slate-600 mb-1">Win Rate</p>
            <p className="text-3xl font-bold text-green-600">{winRate}%</p>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <p className="text-sm text-slate-600 mb-1">Won</p>
            <p className="text-3xl font-bold text-slate-900">{won.length}</p>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <p className="text-sm text-slate-600 mb-1">Lost</p>
            <p className="text-3xl font-bold text-slate-900">{lost.length}</p>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <p className="text-sm text-slate-600 mb-1">Dropped</p>
            <p className="text-3xl font-bold text-slate-900">{dropped.length}</p>
          </div>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-slate-900 mb-4">Deal Outcome Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                dataKey="value"
                label={(entry) => `${entry.name}: ${entry.value}`}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderRevenueReport = () => {
    if (!reportData) return null;
    const { revenue_by_pillar } = reportData.charts;

    return (
      <div className="space-y-6" data-testid="revenue-report">
        <div>
          <h3 className="text-xl font-semibold text-slate-900 mb-4">Revenue by Service Pillar</h3>
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={revenue_by_pillar}
                cx="50%"
                cy="50%"
                outerRadius={120}
                dataKey="value"
                label={(entry) => `${entry.pillar}: ${formatCurrency(entry.value)}`}
              >
                {revenue_by_pillar && revenue_by_pillar.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatCurrency(value)} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {revenue_by_pillar && revenue_by_pillar.map((pillar, index) => (
            <div key={pillar.pillar} className="bg-slate-50 rounded-lg p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">{pillar.pillar}</p>
                <p className="text-xl font-bold text-slate-900">{formatCurrency(pillar.value)}</p>
              </div>
              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderForecastReport = () => {
    if (!reportData) return null;
    const { deals } = reportData;

    const activePipeline = deals.filter(d =>
      !["Won", "Lost", "Dropped"].includes(d.stage)
    );

    const totalPipeline = activePipeline.reduce((sum, d) => sum + d.total_value, 0);
    const totalWeighted = activePipeline.reduce((sum, d) => sum + (d.weighted_value || 0), 0);
    const avgDealSize = activePipeline.length > 0 ? totalPipeline / activePipeline.length : 0;

    return (
      <div className="space-y-6" data-testid="forecast-report">
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <p className="text-sm text-slate-600 mb-1">Total Pipeline</p>
            <p className="text-3xl font-bold text-slate-900">{formatCurrency(totalPipeline)}</p>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <p className="text-sm text-slate-600 mb-1">Weighted Forecast</p>
            <p className="text-3xl font-bold text-blue-600">{formatCurrency(totalWeighted)}</p>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <p className="text-sm text-slate-600 mb-1">Avg Deal Size</p>
            <p className="text-3xl font-bold text-slate-900">{formatCurrency(avgDealSize)}</p>
          </div>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-slate-900 mb-4">Top Opportunities</h3>
          <div className="space-y-3">
            {activePipeline
              .sort((a, b) => b.weighted_value - a.weighted_value)
              .slice(0, 10)
              .map((deal) => (
                <div key={deal._id || deal.project_title} className="bg-slate-50 rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">{deal.project_title}</p>
                    <p className="text-sm text-slate-600">{deal.stage} • {deal.probability}%</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-slate-900">{formatCurrency(deal.total_value)}</p>
                    <p className="text-sm text-blue-600">Weighted: {formatCurrency(deal.weighted_value || 0)}</p>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="reports-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Reports</h1>
          <p className="text-slate-500 mt-1">Business intelligence and analytics</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            data-testid="year-select"
          >
            <option value={2022}>2022</option>
            <option value={2023}>2023</option>
            <option value={2024}>2024</option>
            <option value={2025}>2025</option>
            <option value={2026}>2026</option>
          </select>
          <button className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50" data-testid="export-pdf-btn">
            <Download size={18} />
            Export PDF
          </button>
        </div>
      </div>

      <div className="flex gap-4">
        <div className="w-64 bg-white rounded-lg border border-slate-200 p-4">
          <h3 className="font-semibold text-slate-900 mb-3">Report Types</h3>
          <div className="space-y-1">
            {reports.map((report) => (
              <button
                key={report.id}
                onClick={() => setActiveReport(report.id)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeReport === report.id
                    ? "bg-primary text-white"
                    : "text-slate-700 hover:bg-slate-50"
                }`}
                data-testid={`report-tab-${report.id}`}
              >
                {report.name}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 bg-white rounded-lg border border-slate-200 p-6">
          {activeReport === "pipeline" && renderPipelineReport()}
          {activeReport === "winloss" && renderWinLossReport()}
          {activeReport === "revenue" && renderRevenueReport()}
          {activeReport === "forecast" && renderForecastReport()}
          {["yoy", "activity", "client"].includes(activeReport) && (
            <div className="text-center py-12" data-testid="report-coming-soon">
              <FileText size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-600">{reports.find(r => r.id === activeReport)?.name} coming soon</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;
